const CART_INPUT_CLASS = "casebang-global-accessory-cart-input";
const PRIMARY_PROMOTED_ATTRIBUTE = "data-casebang-global-accessory-promoted";
const GLOBAL_SELECTION_CONTRACT = "__casebangGlobalAccessorySelection";
const PURCHASE_STATE_ATTRIBUTE =
  "data-casebang-global-accessory-purchase-state";
const PREVIEW_STAGE_SELECTOR = ".casebang-phone-preview-stage";
const FIXED_STAGE_CLASS = "casebang-phone-preview-stage--fixed";
const PHONE_SELECTOR = ".casebang-phone-preview-main";
const STAND_SELECTOR = ".casebang-phone-accessory-preview";
const FORMAL_VISUAL_AREA_SELECTOR = ".casebang-phone-visual-area";
const SELECTOR_ORIGIN = "https://casebang.work/api/storefront-selector";

export const buildPc2CartLines = (mainVariantIds, selectedByType) => [
  ...mainVariantIds
    .filter(Boolean)
    .map((id) => ({ id: String(id), kind: "main" })),
  ...Object.values(selectedByType)
    .filter((variant) => variant?.shopifyVariantId)
    .map((variant) => ({
      id: String(variant.shopifyVariantId),
      quantity: 1,
      kind: "global_accessory",
    })),
];

const createHiddenInput = (form, name, value, kind) => {
  const input = document.createElement("input");
  input.type = "hidden";
  input.name = name;
  input.value = String(value);
  input.className = CART_INPUT_CLASS;
  input.dataset.casebangCartLineKind = kind;
  form.appendChild(input);
};

const mainVariantState = (form) => {
  const multiInputs = [
    ...form.querySelectorAll(".casebang-multi-item-input"),
  ].filter((input) => input.name.endsWith("[id]") && input.value);
  if (multiInputs.length) {
    return {
      ids: multiInputs.map((input) => input.value),
      nextIndex:
        Math.max(
          ...multiInputs.map((input) =>
            Number(input.name.match(/^items\[(\d+)]/)?.[1] || -1),
          ),
        ) + 1,
      usesMultiInputs: true,
      primaryInput: form.querySelector(
        '[name="id"], [data-primary-input="true"]',
      ),
    };
  }

  const primaryInput = form.querySelector(
    '[name="id"], [data-primary-input="true"]',
  );
  return {
    ids: primaryInput?.value ? [primaryInput.value] : [],
    nextIndex: 0,
    usesMultiInputs: false,
    primaryInput,
  };
};

export const syncGlobalAccessoryCartInputs = (form, selectedByType) => {
  form
    .querySelectorAll(`.${CART_INPUT_CLASS}`)
    .forEach((input) => input.remove());
  const selected = Object.values(selectedByType).filter(
    (variant) => variant?.shopifyVariantId,
  );
  const main = mainVariantState(form);

  if (!selected.length) {
    if (
      main.primaryInput?.hasAttribute(PRIMARY_PROMOTED_ATTRIBUTE) &&
      !main.usesMultiInputs
    ) {
      main.primaryInput.name = "id";
      main.primaryInput.removeAttribute("data-primary-input");
      main.primaryInput.removeAttribute(PRIMARY_PROMOTED_ATTRIBUTE);
    }
    return buildPc2CartLines(main.ids, {});
  }

  let nextIndex = main.nextIndex;
  if (!main.usesMultiInputs && main.primaryInput && main.ids.length) {
    main.primaryInput.removeAttribute("name");
    main.primaryInput.setAttribute("data-primary-input", "true");
    main.primaryInput.setAttribute(PRIMARY_PROMOTED_ATTRIBUTE, "true");
    createHiddenInput(form, `items[${nextIndex}][id]`, main.ids[0], "main");
    nextIndex += 1;
  } else if (!main.ids.length && main.primaryInput) {
    main.primaryInput.removeAttribute("name");
    main.primaryInput.setAttribute("data-primary-input", "true");
    main.primaryInput.setAttribute(PRIMARY_PROMOTED_ATTRIBUTE, "true");
  }

  for (const variant of selected) {
    createHiddenInput(
      form,
      `items[${nextIndex}][id]`,
      variant.shopifyVariantId,
      "global_accessory",
    );
    createHiddenInput(
      form,
      `items[${nextIndex}][quantity]`,
      1,
      "global_accessory",
    );
    nextIndex += 1;
  }

  return buildPc2CartLines(main.ids, selectedByType);
};

export const getSelectedGlobalAccessoryPreviewVariant = (selectedByType) =>
  Object.values(selectedByType).find((variant) => variant?.image) || null;

export const hasSelectedGlobalAccessory = (selectedByType) =>
  Object.values(selectedByType || {}).some((variant) =>
    Boolean(variant?.shopifyVariantId),
  );

export const hasValidPc2Selection = (mainVariantIds, selectedByType) =>
  buildPc2CartLines(mainVariantIds, selectedByType).length > 0;

export const canClearGlobalAccessorySelection = (
  mainVariantIds,
  selectedByType,
  typeCode,
) => {
  const remainingSelections = { ...selectedByType, [typeCode]: null };
  return hasValidPc2Selection(mainVariantIds, remainingSelections);
};

const pricingForId = (pricingById, id) =>
  pricingById instanceof Map
    ? pricingById.get(String(id))
    : pricingById?.[String(id)];

export const calculatePc2PurchaseTotals = (
  mainVariantIds,
  selectedByType,
  pricingById = {},
) => {
  let price = 0;
  let compareAtPrice = 0;
  let mainPricingComplete = true;

  for (const id of mainVariantIds.filter(Boolean)) {
    const pricing = pricingForId(pricingById, id);
    if (!pricing || !Number.isFinite(Number(pricing.price))) {
      mainPricingComplete = false;
      continue;
    }
    const variantPrice = Number(pricing.price);
    price += variantPrice;
    compareAtPrice += Number(pricing.compareAtPrice ?? variantPrice);
  }

  for (const variant of Object.values(selectedByType || {})) {
    if (!variant?.shopifyVariantId || !Number.isFinite(Number(variant.price)))
      continue;
    const variantPrice = Number(variant.price);
    price += variantPrice;
    compareAtPrice += Number(variant.compareAtPrice ?? variantPrice);
  }

  return { price, compareAtPrice, mainPricingComplete };
};

export const hasCompositionPreview = ({
  hasPhone = false,
  hasStand = false,
} = {}) => hasPhone || hasStand;

export const shouldRenderGlobalAccessoryStandalone = ({
  hasPhone = false,
  hasGlobalAccessory = false,
  hasStand = false,
} = {}) => hasGlobalAccessory && !hasCompositionPreview({ hasPhone, hasStand });

export const getGlobalAccessoryPreviewPreset = ({
  hasPhone = false,
  hasGlobalAccessory = false,
  hasStand = false,
} = {}) => {
  if (hasPhone) return hasStand ? "phone-stand" : "phone";
  if (hasStand) return "stand-only";
  if (hasGlobalAccessory) return "global-only";
  return "empty";
};

export const isGlobalAccessoryDisableSelected = (selectedByType, typeCode) =>
  !selectedByType?.[typeCode];

export const clearGlobalAccessorySelection = (selectedByType, typeCode) => {
  selectedByType[typeCode] = null;
  return selectedByType;
};

export const isGlobalAccessoryOptionSelected = (
  selectedByType,
  typeCode,
  variantId,
) => selectedByType?.[typeCode]?.id === variantId;

const marketPrices = () => window.CasebangPc2MarketPrices;
const formatVariantPrice = (id) =>
  marketPrices()?.format(marketPrices().lookup(id)?.price) || "—";

const updateDisplayedPurchasePrice = (totals) => {
  const container = document.querySelector(
    ".casebang-next-buy-buttons-container .price",
  );
  if (!container) return;
  const price = totals?.price;
  const compareAtPrice = totals?.compareAtPrice;
  const onSale = price && compareAtPrice &&
    Number(compareAtPrice.amount) > Number(price.amount);
  if (!onSale) {
    container
      .querySelectorAll(".price-item")
      .forEach((item) => (item.textContent = marketPrices()?.format(price) || "—"));
    container.classList.remove("price--on-sale");
    return;
  }
  container
    .querySelectorAll(".price-item--sale")
    .forEach((item) => (item.textContent = marketPrices().format(price)));
  container
    .querySelectorAll(".price-item--regular")
    .forEach((item) => (item.textContent = marketPrices().format(compareAtPrice)));
  container.classList.add("price--on-sale");
};

const updatePurchaseButton = (form, hasPurchasableSelection) => {
  const productForm = form.closest("product-form");
  const button = productForm?.querySelector('[type="submit"]');
  if (!button) return;
  button.setAttribute(
    PURCHASE_STATE_ATTRIBUTE,
    String(hasPurchasableSelection),
  );
  if (hasPurchasableSelection) {
    button.removeAttribute("disabled");
    button.removeAttribute("aria-disabled");
  } else {
    button.setAttribute("disabled", "disabled");
    button.setAttribute("aria-disabled", "true");
  }
};

export const syncGlobalAccessoryPurchaseState = (
  form,
  mainVariantIds,
  selectedByType,
) => {
  const variantIds = buildPc2CartLines(mainVariantIds, selectedByType)
    .map((line) => line.id);
  const revision = (Number(form.dataset.casebangMarketPriceRevision) || 0) + 1;
  form.dataset.casebangMarketPriceRevision = String(revision);
  updateDisplayedPurchasePrice(null);
  if (variantIds.length && marketPrices()) {
    marketPrices().resolve(variantIds).then((totals) => {
      if (form.dataset.casebangMarketPriceRevision === String(revision)) {
        updateDisplayedPurchasePrice(totals);
      }
    }).catch(() => {
      if (form.dataset.casebangMarketPriceRevision === String(revision)) {
        updateDisplayedPurchasePrice(null);
      }
    });
  }
  const hasPurchasableSelection = hasValidPc2Selection(
    mainVariantIds,
    selectedByType,
  );
  updatePurchaseButton(form, hasPurchasableSelection);
  return { hasPurchasableSelection };
};

export const createPurchaseSyncScheduler = (
  sync,
  requestFrame = requestAnimationFrame,
  cancelFrame = cancelAnimationFrame,
) => {
  let outerFrame = 0;
  let innerFrame = 0;
  const schedule = () => {
    if (outerFrame || innerFrame) return;
    outerFrame = requestFrame(() => {
      outerFrame = 0;
      innerFrame = requestFrame(() => {
        innerFrame = 0;
        sync();
      });
    });
  };
  const cancel = () => {
    if (outerFrame) cancelFrame(outerFrame);
    if (innerFrame) cancelFrame(innerFrame);
    outerFrame = 0;
    innerFrame = 0;
  };
  return { schedule, cancel };
};

const variantLabel = (product, variant, productCount) =>
  productCount > 1 ? `${product.name} · ${variant.name}` : variant.name;

const consumerLabelForGroup = (group) => group.productType.name;

const createDisableIcon = () => {
  const icon = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  icon.setAttribute("width", "24");
  icon.setAttribute("height", "24");
  icon.setAttribute("viewBox", "0 0 24 24");
  icon.setAttribute("fill", "none");
  icon.setAttribute("stroke", "currentColor");
  icon.setAttribute("stroke-width", "2");
  icon.setAttribute("stroke-linecap", "round");
  icon.setAttribute("stroke-linejoin", "round");
  icon.setAttribute("aria-hidden", "true");
  icon.setAttribute(
    "class",
    "ca:absolute ca:top-1/2 ca:left-1/2 ca:size-6! ca:-translate-x-1/2 ca:-translate-y-1/2 ca:stroke-1 ca:font-light ca:text-gray-500",
  );
  const line = document.createElementNS("http://www.w3.org/2000/svg", "path");
  line.setAttribute("d", "M4.929 4.929 19.07 19.071");
  const circle = document.createElementNS(
    "http://www.w3.org/2000/svg",
    "circle",
  );
  circle.setAttribute("cx", "12");
  circle.setAttribute("cy", "12");
  circle.setAttribute("r", "10");
  icon.append(line, circle);
  return icon;
};

const createDisableButton = (selectedByType, typeCode, onChange, canClear) => {
  const button = document.createElement("button");
  button.type = "button";
  button.className =
    "casebang-accessory-swatch-option casebang-global-accessory-option casebang-global-accessory-option--disable ca:flex ca:min-w-0 ca:flex-col ca:cursor-pointer ca:focus:outline-none ca:focus-visible:ring-2 ca:focus-visible:ring-offset-1";
  button.dataset.disable = "true";
  button.dataset.variantId = "";

  const visual = document.createElement("div");
  visual.className =
    "casebang-global-accessory-option__visual ca:relative ca:w-full ca:aspect-square ca:overflow-hidden ca:rounded-xl ca:md:rounded-[16px] ca:border-primary/20 ca:border ca:transition-all ca:bg-background";
  visual.appendChild(createDisableIcon());

  const label = document.createElement("div");
  label.className =
    "casebang-global-accessory-option__label ca:text-[11px] ca:leading-tight ca:text-primary ca:mt-2 ca:text-ellipsis ca:whitespace-nowrap ca:overflow-hidden ca:w-full";
  label.textContent = "Disable";
  button.append(visual, label);
  button.addEventListener("click", () => {
    if (!canClear(typeCode)) return;
    clearGlobalAccessorySelection(selectedByType, typeCode);
    onChange();
  });
  return button;
};

const createVariantButton = (
  product,
  variant,
  productCount,
  selectedByType,
  typeCode,
  onChange,
) => {
  const button = document.createElement("button");
  button.type = "button";
  button.className =
    "casebang-accessory-swatch-option casebang-global-accessory-option ca:flex ca:min-w-0 ca:flex-col ca:cursor-pointer ca:focus:outline-none ca:focus-visible:ring-2 ca:focus-visible:ring-offset-1";
  button.dataset.variantId = variant.id;
  button.disabled = !variant.available;

  const visual = document.createElement("div");
  visual.className =
    "casebang-global-accessory-option__visual ca:relative ca:w-full ca:aspect-square ca:overflow-hidden ca:rounded-xl ca:md:rounded-[16px] ca:border-primary/20 ca:border ca:transition-all ca:bg-background";
  if (variant.image) {
    const image = document.createElement("img");
    image.src = variant.image;
    image.alt = variantLabel(product, variant, productCount);
    image.className = "ca:size-full ca:object-contain";
    image.sizes = "96px";
    image.loading = "lazy";
    image.decoding = "async";
    image.fetchPriority = "low";
    visual.appendChild(image);
  }

  const label = document.createElement("div");
  label.className =
    "casebang-global-accessory-option__label ca:text-[11px] ca:leading-tight ca:text-primary ca:mt-2 ca:text-ellipsis ca:whitespace-nowrap ca:overflow-hidden ca:w-full";
  label.textContent = variantLabel(product, variant, productCount);

  const price = document.createElement("div");
  price.className =
    "casebang-global-accessory-option__price price-item ca:text-[11px] ca:leading-none ca:font-light ca:text-gray-500 ca:mt-1 ca:text-ellipsis ca:whitespace-nowrap ca:overflow-hidden ca:w-full";
  price.dataset.price = "true";
  price.dataset.shopifyVariantId = String(variant.shopifyVariantId || "");
  price.textContent = variant.available
    ? formatVariantPrice(variant.shopifyVariantId)
    : "Sold out";

  button.append(visual, label, price);
  button.addEventListener("click", () => {
    selectedByType[typeCode] = variant;
    onChange();
  });
  return button;
};

const waitForNativeOptionList = (selectorRoot) =>
  new Promise((resolve) => {
    let observer;
    const finish = (optionList) => {
      observer?.disconnect();
      window.removeEventListener("pagehide", handlePageHide);
      resolve(optionList);
    };
    const findOptionList = () => {
      if (selectorRoot.dataset.casebangPc2Integrated !== "true") return null;
      const integratedPanel = selectorRoot.querySelector(
        ".casebang-integrated-panel",
      );
      if (!integratedPanel) return null;
      let optionList = integratedPanel.querySelector(
        ".casebang-accessory-options",
      );
      if (!optionList) {
        optionList = document.createElement("div");
        optionList.className =
          "ca:grid ca:grid-cols-1 ca:gap-4 casebang-accessory-options";
        integratedPanel.appendChild(optionList);
      }
      return optionList;
    };
    const handlePageHide = () => finish(null);
    const existing = findOptionList();
    if (existing) {
      finish(existing);
      return;
    }
    observer = new MutationObserver(() => {
      const optionList = findOptionList();
      if (optionList) finish(optionList);
    });
    observer.observe(selectorRoot, {
      attributes: true,
      childList: true,
      subtree: true,
    });
    window.addEventListener("pagehide", handlePageHide, { once: true });
  });

const renderPanel = (
  optionList,
  groups,
  selectedByType,
  onChange,
  canClear,
) => {
  for (const group of groups) {
    const groupElement = document.createElement("div");
    groupElement.className = `casebang-option-group casebang-option-group-${group.productType.code} casebang-global-accessories__group ca:space-y-3`;
    groupElement.dataset.productType = group.productType.code;
    groupElement.setAttribute("role", "group");
    groupElement.setAttribute("aria-label", consumerLabelForGroup(group));

    const groupHeading = document.createElement("div");
    groupHeading.className =
      "casebang-option-heading casebang-global-accessories__group-heading ca:flex ca:items-center ca:justify-between ca:gap-3 ca:leading-normal ca:font-extrabold ca:peer-disabled:cursor-not-allowed ca:peer-disabled:opacity-70";
    const groupValue = document.createElement("span");
    groupValue.className =
      "casebang-global-accessories__group-value ca:max-w-[55%] ca:truncate ca:text-xs ca:font-medium ca:text-gray-500";
    groupHeading.append(
      document.createTextNode(consumerLabelForGroup(group)),
      groupValue,
    );

    const track = document.createElement("div");
    track.className =
      "casebang-global-accessories__track casebang-accessory-swatch-track ca:flex ca:gap-2";
    track.appendChild(
      createDisableButton(
        selectedByType,
        group.productType.code,
        onChange,
        canClear,
      ),
    );
    for (const product of group.products) {
      for (const variant of product.variants) {
        track.appendChild(
          createVariantButton(
            product,
            variant,
            group.products.length,
            selectedByType,
            group.productType.code,
            onChange,
          ),
        );
      }
    }
    groupElement.append(groupHeading, track);
    optionList.appendChild(groupElement);
  }

  return optionList;
};

const updatePanelSelection = (panel, selectedByType) => {
  panel.querySelectorAll("[data-product-type]").forEach((group) => {
    const selected = selectedByType[group.dataset.productType];
    const value = group.querySelector(
      ".casebang-global-accessories__group-value",
    );
    if (value) value.textContent = selected?.name || "Disable";
  });
  panel
    .querySelectorAll(".casebang-global-accessory-option")
    .forEach((button) => {
      const typeCode = button.closest("[data-product-type]")?.dataset
        .productType;
      const selected =
        button.dataset.disable === "true"
          ? isGlobalAccessoryDisableSelected(selectedByType, typeCode)
          : isGlobalAccessoryOptionSelected(
              selectedByType,
              typeCode,
              button.dataset.variantId,
            );
      button.setAttribute("aria-pressed", String(selected));
      const visual = button.querySelector(
        ".casebang-global-accessory-option__visual",
      );
      visual?.classList.toggle("ca:border-primary/20", !selected);
      visual?.classList.toggle("ca:border-primary", selected);
      visual?.classList.toggle("ca:ring-2", selected);
      visual?.classList.toggle("ca:ring-primary/15", selected);
      visual?.classList.toggle("ca:hover:border-primary/50", !selected);
    });
};

const createPreviewMount = (mode) => {
  const mount = document.createElement("div");
  mount.className = `casebang-preview-slot casebang-preview-slot--global-accessory casebang-global-accessory-preview casebang-global-accessory-preview--${mode}`;
  mount.dataset.casebangGlobalAccessoryPreview = mode;
  mount.dataset.casebangPreviewSlot = "global-accessory";
  mount.setAttribute("aria-hidden", "true");
  const visual = document.createElement("span");
  visual.className = "casebang-global-accessory-preview__visual";
  const image = document.createElement("img");
  image.alt = "";
  image.draggable = false;
  image.decoding = "async";
  visual.appendChild(image);
  mount.appendChild(visual);
  return mount;
};

const updatePreviewMount = (mount, variant) => {
  const image = mount.querySelector("img");
  const imageUrl = variant?.image || "";
  if (image?.getAttribute("src") !== imageUrl) {
    if (imageUrl) image.setAttribute("src", imageUrl);
    else image?.removeAttribute("src");
  }
  mount.dataset.variantId = variant?.id || "";
};

const annotateNativePreviewSlots = (stage) => {
  const fixed = stage.classList.contains(FIXED_STAGE_CLASS);
  const phoneSlot = fixed
    ? stage.querySelector(PHONE_SELECTOR)
    : stage.querySelector(".casebang-phone-slot");
  const standSlot = fixed
    ? stage.querySelector(STAND_SELECTOR)
    : stage.querySelector(".casebang-accessory-slot");
  phoneSlot?.setAttribute("data-casebang-preview-slot", "phone");
  standSlot?.setAttribute("data-casebang-preview-slot", "stand");
  stage
    .querySelectorAll(".casebang-image-layer-RS")
    .forEach((layer) =>
      layer.setAttribute("data-casebang-preview-slot", "stand-back-cover"),
    );
  stage
    .querySelectorAll(".casebang-image-layer-SB, .casebang-image-layer-ZJ")
    .forEach((layer) =>
      layer.setAttribute("data-casebang-preview-slot", "base-phone-stand"),
    );
};

export const reconcileGlobalAccessoryPreviewStage = (stage, mount, variant) => {
  if (!stage) {
    mount.remove();
    updatePreviewMount(mount, null);
    return {
      active: false,
      fixed: false,
      hasPhone: false,
      hasStand: false,
      preset: "empty",
    };
  }

  annotateNativePreviewSlots(stage);
  const fixed = stage.classList.contains(FIXED_STAGE_CLASS);
  const hasPhone = Boolean(stage.querySelector(PHONE_SELECTOR));
  const hasStand = Boolean(stage.querySelector(STAND_SELECTOR));
  const selectedGlobalAccessory = Boolean(variant?.image);
  const active = shouldRenderGlobalAccessoryStandalone({
    hasPhone,
    hasGlobalAccessory: selectedGlobalAccessory,
    hasStand,
  });
  const preset = getGlobalAccessoryPreviewPreset({
    hasPhone,
    hasGlobalAccessory: selectedGlobalAccessory,
    hasStand,
  });

  stage.dataset.casebangPreviewPreset = preset;
  stage.dataset.casebangHasPhone = String(hasPhone);
  stage.dataset.casebangHasGlobalAccessory = String(selectedGlobalAccessory);
  stage.dataset.casebangGlobalAccessoryStandalone = String(active);
  stage.dataset.casebangHasStand = String(hasStand);

  if (!active) {
    mount.remove();
    updatePreviewMount(mount, null);
    return { active, fixed, hasPhone, hasStand, preset };
  }

  const host = fixed
    ? stage
    : stage.querySelector(FORMAL_VISUAL_AREA_SELECTOR) || stage;
  updatePreviewMount(mount, variant);
  if (mount.parentElement !== host) host.appendChild(mount);
  return { active, fixed, hasPhone, hasStand, preset };
};

const stageContainsNativeChange = (node) => {
  if (!(node instanceof Element)) return false;
  if (node.matches("[data-casebang-global-accessory-preview]")) return false;
  return (
    node.matches(`${PREVIEW_STAGE_SELECTOR}, ${STAND_SELECTOR}`) ||
    Boolean(node.querySelector(`${PREVIEW_STAGE_SELECTOR}, ${STAND_SELECTOR}`))
  );
};

export const createGlobalAccessoryPreviewController = (getSelectedByType) => {
  const formalMount = createPreviewMount("formal");
  const fixedMount = createPreviewMount("fixed");
  let frame = 0;
  let destroyed = false;

  const sync = () => {
    if (destroyed) return;
    frame = 0;
    const variant =
      getSelectedGlobalAccessoryPreviewVariant(getSelectedByType());
    const stages = [...document.querySelectorAll(PREVIEW_STAGE_SELECTOR)];
    reconcileGlobalAccessoryPreviewStage(
      stages.find((stage) => !stage.classList.contains(FIXED_STAGE_CLASS)),
      formalMount,
      variant,
    );
    reconcileGlobalAccessoryPreviewStage(
      stages.find((stage) => stage.classList.contains(FIXED_STAGE_CLASS)),
      fixedMount,
      variant,
    );
  };

  const schedule = () => {
    if (frame || destroyed) return;
    frame = requestAnimationFrame(sync);
  };

  const observer = new MutationObserver((records) => {
    if (
      records.some((record) =>
        [...record.addedNodes, ...record.removedNodes].some(
          stageContainsNativeChange,
        ),
      )
    ) {
      schedule();
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  const handleSelectorClick = (event) => {
    if (
      event.target instanceof Element &&
      event.target.closest("#casebang-variants button")
    ) {
      schedule();
    }
  };
  document.addEventListener("click", handleSelectorClick, true);
  sync();

  return {
    sync,
    destroy: () => {
      if (destroyed) return;
      destroyed = true;
      if (frame) cancelAnimationFrame(frame);
      observer.disconnect();
      document.removeEventListener("click", handleSelectorClick, true);
      formalMount.remove();
      fixedMount.remove();
    },
  };
};

const start = async () => {
  const pc2Root = document.querySelector("[data-casebang-pc2-root]");
  const selectorRoot = pc2Root?.querySelector("#casebang-variants");
  if (
    !pc2Root ||
    !selectorRoot ||
    selectorRoot.dataset.casebangGlobalAccessories === "true"
  ) {
    return;
  }

  let payload;
  try {
    const pending = window.__casebangSelectorProductRequest;
    const productId = selectorRoot.dataset.productId;
    if (!pending?.promise && !productId) return;
    payload = pending?.promise
      ? await pending.promise
      : await fetch(
          `${SELECTOR_ORIGIN}/products/${encodeURIComponent(productId)}`,
        ).then((response) => {
          if (!response.ok) throw new Error("Network response was not ok");
          return response.json();
        });
  } catch {
    return;
  }
  const groups = Array.isArray(payload?.globalAccessories)
    ? payload.globalAccessories
    : [];
  if (!groups.length) return;

  const productFormId = selectorRoot.dataset.productFormId;
  const form = productFormId ? document.getElementById(productFormId) : null;
  if (!form) return;

  const optionList = await waitForNativeOptionList(selectorRoot);
  if (!optionList || selectorRoot.dataset.casebangGlobalAccessories === "true")
    return;
  selectorRoot.dataset.casebangGlobalAccessories = "true";
  const selectedByType = Object.fromEntries(
    groups.map((group) => [group.productType.code, null]),
  );
  const selectionContract = {
    getSelectedByType: () => selectedByType,
    getSelectedVariants: () => Object.values(selectedByType).filter(Boolean),
    hasValidSelection: () => hasSelectedGlobalAccessory(selectedByType),
  };
  window[GLOBAL_SELECTION_CONTRACT] = selectionContract;
  const previewController = createGlobalAccessoryPreviewController(
    () => selectedByType,
  );
  let panel;
  const canClear = (typeCode) =>
    canClearGlobalAccessorySelection(
      mainVariantState(form).ids,
      selectedByType,
      typeCode,
    );
  const sync = () => {
    const cartLines = syncGlobalAccessoryCartInputs(form, selectedByType);
    syncGlobalAccessoryPurchaseState(
      form,
      cartLines.filter((line) => line.kind === "main").map((line) => line.id),
      selectedByType,
    );
    updatePanelSelection(panel, selectedByType);
    previewController.sync();
  };

  panel = renderPanel(optionList, groups, selectedByType, sync, canClear);
  const visibleVariants = groups.flatMap((group) => group.products.flatMap((product) =>
    product.variants.filter((variant) => variant.available && variant.shopifyVariantId)
      .map((variant) => variant.shopifyVariantId)));
  const refreshGlobalPrices = (attempt = 0) => {
    marketPrices()?.request(visibleVariants)?.then(() => {
      let missing = false;
      panel.querySelectorAll("[data-shopify-variant-id]").forEach((item) => {
        item.textContent = formatVariantPrice(item.dataset.shopifyVariantId);
        if (item.textContent === "—") missing = true;
      });
      if (missing && attempt < 2) {
        window.setTimeout(() => refreshGlobalPrices(attempt + 1), 1000);
      }
    });
  };
  refreshGlobalPrices();
  sync();

  const purchaseScheduler = createPurchaseSyncScheduler(sync);
  const handlePurchaseInteraction = (event) => {
    if (
      event.target instanceof Element &&
      event.target.closest(
        "#casebang-variants button, .casebang-global-accessory-option",
      )
    ) {
      purchaseScheduler.schedule();
    }
  };
  document.addEventListener("click", handlePurchaseInteraction, true);
  form.addEventListener("submit", sync, true);
  form.addEventListener("change", purchaseScheduler.schedule);
  window.addEventListener(
    "pagehide",
    () => {
      purchaseScheduler.cancel();
      document.removeEventListener("click", handlePurchaseInteraction, true);
      form.removeEventListener("submit", sync, true);
      form.removeEventListener("change", purchaseScheduler.schedule);
      previewController.destroy();
      if (window[GLOBAL_SELECTION_CONTRACT] === selectionContract) {
        delete window[GLOBAL_SELECTION_CONTRACT];
      }
    },
    { once: true },
  );
};

if (typeof document !== "undefined") {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start, { once: true });
  } else {
    start();
  }
}
